import React, { useState, useEffect } from "react";
import { auth, db, storage } from "./firebase";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import { useNavigate } from "react-router-dom";
import { onAuthStateChanged } from "firebase/auth";

const MAX_IMAGES = 10;

const dedupeUrls = (arr) => Array.from(new Set((arr || []).filter(Boolean)));

const EditInvestorProfile = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);
  const [formData, setFormData] = useState({
    fullName: "",
    businessName: "",
    location: "",
    industry: "",
    investmentRange: "",
    bio: "",
    photoUrls: [],
  });
  const [error, setError] = useState(null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        navigate("/login");
        return;
      }
      try {
        const userRef = doc(db, "users", user.uid);
        const userSnap = await getDoc(userRef);
        if (userSnap.exists()) {
          const data = userSnap.data();
          if (data.role !== "investor") {
            navigate("/dashboard");
            return;
          }

          // Merge possible legacy fields: profileImages and photoUrls
          const mergedImages = dedupeUrls([
            ...(data.profileImages || []),
            ...(data.photoUrls || []),
          ]).slice(0, MAX_IMAGES);

          setCurrentUser({ uid: user.uid, ...data });
          setFormData({
            fullName: data.fullName || "",
            businessName: data.businessName || "",
            location: data.location || "",
            industry: data.industry || "",
            investmentRange: data.investmentRange || "",
            bio: data.bio || "",
            photoUrls: mergedImages,
          });
          setLoading(false);
        } else {
          setError("Profile not found.");
          setLoading(false);
        }
      } catch (err) {
        setError(err.message);
        setLoading(false);
      }
    });
    return () => unsubscribe();
  }, [navigate]);

  const handleChange = (e) =>
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  // Upload images: append without overriding; dedupe and cap to MAX_IMAGES
  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    if (!currentUser?.uid) {
      alert("Not authenticated");
      return;
    }

    const currentCount = formData.photoUrls.length;
    const capacity = MAX_IMAGES - currentCount;
    if (capacity <= 0) {
      alert(`You can upload up to ${MAX_IMAGES} images.`);
      return;
    }

    const selected = files.slice(0, capacity);
    if (files.length > selected.length) {
      alert(`Only ${capacity} more image(s) allowed; extra file(s) ignored.`);
    }

    setUploading(true);
    const newUrls = [];

    for (let i = 0; i < selected.length; i++) {
      const file = selected[i];
      const safeName = (file.name || `image_${i}`).replace(/\s+/g, "_");
      const uniqueKey = `${Date.now()}_${i}_${Math.random().toString(36).slice(2)}`;
      const storagePath = `investorPhotos/${currentUser.uid}/${uniqueKey}_${safeName}`;
      const storageRef = ref(storage, storagePath);
      const uploadTask = uploadBytesResumable(storageRef, file);

      // Await this file's upload completion
      // eslint-disable-next-line no-await-in-loop
      await new Promise((resolve, reject) => {
        uploadTask.on(
          "state_changed",
          null,
          (err) => reject(err),
          async () => {
            try {
              const url = await getDownloadURL(storageRef);
              newUrls.push(url);
              resolve();
            } catch (err) {
              reject(err);
            }
          }
        );
      });
    }

    const merged = dedupeUrls([...(formData.photoUrls || []), ...newUrls]).slice(
      0,
      MAX_IMAGES
    );

    setFormData((prev) => ({ ...prev, photoUrls: merged }));
    setUploading(false);
  };

  // Remove image (updates state; storage object is not deleted here)
  const removeImage = (index) => {
    setFormData((prev) => ({
      ...prev,
      photoUrls: prev.photoUrls.filter((_, i) => i !== index),
    }));
  };

  // Submit profile
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    try {
      if (!currentUser) throw new Error("Not authenticated");
      const userRef = doc(db, "users", currentUser.uid);

      const finalImages = dedupeUrls(formData.photoUrls).slice(0, MAX_IMAGES);

      await updateDoc(userRef, {
        fullName: (formData.fullName || "").trim(),
        businessName: (formData.businessName || "").trim(),
        location: (formData.location || "").trim(),
        industry: (formData.industry || "").trim(),
        investmentRange: (formData.investmentRange || "").trim(),
        bio: (formData.bio || "").trim(),
        // Persist under both keys for compatibility with InvestorCard
        photoUrls: finalImages,
        profileImages: finalImages,
        updatedAt: new Date(),
      });

      alert("✅ Investor profile updated!");
      navigate("/investor-dashboard");
    } catch (err) {
      setError(err.message);
    }
  };

  if (loading) return <div>⏳ Loading...</div>;

  return (
    <div className="max-w-md mx-auto p-6 bg-white shadow rounded">
      <h2 className="text-2xl font-bold mb-4">✏️ Edit Investor Profile</h2>
      {error && <p className="text-red-600 mb-4">{error}</p>}

      {/* Image Manager */}
      <div className="mb-4">
        <div className="flex overflow-x-auto space-x-2 pb-1 scrollbar-hide">
          {formData.photoUrls.map((url, idx) => (
            <div key={idx} className="relative flex-shrink-0 w-40 h-40">
              <div className="w-40 h-40 rounded-full overflow-hidden bg-gray-100 border border-gray-200">
                <img
                  src={url}
                  alt={`Profile ${idx + 1}`}
                  className="w-full h-full object-cover object-center block"
                />
              </div>
              <button
                type="button"
                className="absolute -top-1 -right-1 bg-red-600 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs"
                onClick={() => removeImage(idx)}
                title="Remove"
              >
                ×
              </button>
            </div>
          ))}
          {formData.photoUrls.length < MAX_IMAGES && (
            <label className="w-40 h-40 border-dashed border-2 border-gray-400 flex items-center justify-center rounded-full cursor-pointer text-2xl text-gray-500 hover:bg-gray-50 flex-shrink-0">
              {uploading ? "…" : "+"}
              <input
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                onChange={handleImageUpload}
              />
            </label>
          )}
        </div>
        <p className="text-xs text-gray-500 mt-1">
          {formData.photoUrls.length}/{MAX_IMAGES} images uploaded
        </p>
      </div>

      {/* Profile Form */}
      <form onSubmit={handleSubmit} className="space-y-4">
        <input
          type="text"
          name="fullName"
          placeholder="Full Name"
          value={formData.fullName}
          onChange={handleChange}
          required
          className="w-full border px-3 py-2 rounded"
        />
        <input
          type="text"
          name="businessName"
          placeholder="Business Name"
          value={formData.businessName}
          onChange={handleChange}
          className="w-full border px-3 py-2 rounded"
        />
        <input
          type="text"
          name="location"
          placeholder="Location"
          value={formData.location}
          onChange={handleChange}
          className="w-full border px-3 py-2 rounded"
        />
        <input
          type="text"
          name="industry"
          placeholder="Industry (e.g., Real Estate, Media, Manufacturing)"
          value={formData.industry}
          onChange={handleChange}
          required
          className="w-full border px-3 py-2 rounded"
        />
        <input
          type="text"
          name="investmentRange"
          placeholder="Investment Range (e.g., $1,000 - $50,000)"
          value={formData.investmentRange}
          onChange={handleChange}
          required
          className="w-full border px-3 py-2 rounded"
        />

        <textarea
          name="bio"
          placeholder="Write your Bio..."
          value={formData.bio}
          onChange={handleChange}
          rows={4}
          className="w-full border px-3 py-2 rounded resize-none"
        />

        <button
          type="submit"
          disabled={uploading}
          className="w-full bg-blue-600 text-white py-2 rounded hover:bg-blue-700 disabled:opacity-60"
        >
          Save Changes
        </button>
      </form>

      <style>{`
        .scrollbar-hide::-webkit-scrollbar { display: none; }
        .scrollbar-hide { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>
    </div>
  );
};

export default EditInvestorProfile;